import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { NAV } from "../../constants/siteTestIds";
import { NAV_LINKS } from "../../data/siteContent";

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
    setOpen(false);
  }, [pathname]);

  const isActive = (path) =>
    path === "/" ? pathname === "/" : pathname.startsWith(path);

  const testIdFor = (id) =>
    ({ home: NAV.home, about: NAV.about, podcast: NAV.podcast, books: NAV.books, media: NAV.media, speaking: NAV.speaking, projects: NAV.projects, contact: NAV.contact }[id]);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-white/85 backdrop-blur-2xl border-b border-black/10"
          : "bg-white border-b border-transparent"
      }`}
    >
      <div className="mx-auto max-w-[1600px] px-6 md:px-10 h-[72px] flex items-center justify-between gap-6">
        <Link data-testid={NAV.brand} to="/" className="flex items-center gap-3 group flex-shrink-0">
          <span className="font-mono-ui text-[11px] tracking-[0.3em] text-[#C8102E] group-hover:text-[#a40d25] transition-colors">MS</span>
          <span className="font-display text-xl md:text-2xl text-[#0a0a0a] tracking-tight">Manuel Santana</span>
        </Link>

        <nav className="hidden lg:flex items-center gap-7">
          {NAV_LINKS.map((l) => (
            <Link
              key={l.id}
              to={l.path}
              data-testid={testIdFor(l.id)}
              className={`font-mono-ui text-[11px] tracking-[0.22em] uppercase pb-1 ${
                isActive(l.path) ? "text-[#0a0a0a] nav-link-active" : "text-black/60 hover:text-black"
              } transition-colors`}
            >
              {l.label}
            </Link>
          ))}
        </nav>

        <button
          data-testid={NAV.cta}
          onClick={() => navigate("/contact")}
          className="hidden lg:inline-flex items-center gap-2 bg-[#0a0a0a] hover:bg-transparent border border-[#0a0a0a] text-white hover:text-[#0a0a0a] px-5 py-2.5 text-[11px] font-mono-ui tracking-[0.2em] uppercase transition-all flex-shrink-0"
        >
          Book / Inquire
        </button>

        <button
          data-testid={NAV.mobileToggle}
          onClick={() => setOpen((o) => !o)}
          className="lg:hidden text-[#0a0a0a] p-2"
          aria-label="Toggle menu"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="lg:hidden overflow-hidden bg-white border-t border-black/10"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {NAV_LINKS.map((l) => (
                <Link
                  key={l.id}
                  to={l.path}
                  data-testid={`mobile-${testIdFor(l.id)}`}
                  onClick={() => setOpen(false)}
                  className={`text-left font-display text-2xl ${isActive(l.path) ? "text-[#0a0a0a]" : "text-black/70"} hover:opacity-70 transition-opacity`}
                >
                  {l.label}
                </Link>
              ))}
              <button
                onClick={() => { setOpen(false); navigate("/contact"); }}
                className="mt-4 self-start btn-primary"
              >
                Book / Inquire
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
